'use strict';

import React from 'react';
import { useState, useEffect, useContext } from 'react';
import { Table, Button, OverlayTrigger, Tooltip, Popover } from 'react-bootstrap';

import AppContext from '../AppContext';

import useRequest from '../helper/useRequest';
import { toast } from 'react-toastify';

import Loader from '../helper/Loader';

import TableHeader from '../helper/TableHeader';
import { customFilterFunction } from '../helper/CustomFilter';

import {
    createColumnHelper,
    flexRender,
    getCoreRowModel,
    getSortedRowModel,
    getFilteredRowModel,
    useReactTable,
} from '@tanstack/react-table'

const columnHelper = createColumnHelper();

function Schools() {
    const { request } = useRequest();
    const { GetOrganisationStatus } = useContext(AppContext);

    const [sorting, setSorting] = useState([]);
    const [columnFilters, setColumnFilters] = useState([]);
    const [schools, setSchools] = useState(false);
    const [realSchools, setRealSchools] = useState(false);

    useEffect(() => {
        const fetchData = async () => {
            try {
                let result = await request("/Operator/GetSchools");
                let json = result.data;
                if (json.r != 1) {
                    if (json.message) {
                        toast.error(json.message);
                    }
                    else {
                        toast.error("Es ist ein Fehler aufgetreten.");
                    }
                }
                else {
                    setRealSchools(json.schools);
                }
            }
            catch (ex) {
                console.error(ex);
                toast.error("Es ist ein Fehler aufgetreten.");
            }
        };
        fetchData();
    }, []);

    const columns = [
        columnHelper.accessor('StatusString', {
            header: () => (
                <OverlayTrigger popperConfig={{
                    modifiers: {
                        name: "offset",
                        options: {
                            offset: [0, 20]
                        }
                    }
                }} placement="left" overlay={(
                    <Popover id="business-status-info">
                        <Popover.Header as="h3">Status</Popover.Header>
                        <Popover.Body>
                            <div><span className="bookedCircle me-2"></span>in review</div>
                            <div><span className="bookedCircle booked me-2"></span>approved</div>
                            <div><span className="bookedCircle canceled me-2"></span>rejected</div>
                            <div><span className="bookedCircle deleted me-2"></span>deleted</div>
                        </Popover.Body>
                    </Popover>
                )}>
                    <span>Status</span>
                </OverlayTrigger>
            ),
            cell: info => {
                let statusCircle = <span className="bookedCircle"></span>;
                if (info.getValue() == "approved") {
                    statusCircle = <span className="bookedCircle booked"></span>;
                }
                else if (info.getValue() == "rejected") {
                    statusCircle = <span className="bookedCircle canceled"></span>;
                }
                else if (info.getValue() == "deleted") {
                    statusCircle = <span className="bookedCircle deleted"></span>;
                }

                return (
                    <OverlayTrigger placement="left" overlay={<Tooltip>{info.getValue()}</Tooltip>}>
                        <>{statusCircle} {info.getValue()}</>
                    </OverlayTrigger>
                );
            },
            enableColumnFilter: true,
            filterFn: customFilterFunction,
            meta: {
                filterComponent: "select",
            }
        }),
        columnHelper.accessor('OrganisationName', {
            header: () => <span>Name</span>,
            cell: info => info.getValue(),
            enableColumnFilter: true,
            sortingFn: 'alphanumeric',
        }),
        columnHelper.accessor('Street', {
            header: () => <span>Adresse</span>,
            cell: info => {
                return <div>{info.getValue()}</div>;
            },
            enableColumnFilter: true,
            enableSorting: false,
        }),
        columnHelper.accessor('OrganisationID', {
            header: () => <span></span>,
            cell: info => {
                return <div className="text-end"><Button href={"/Operator/School/" + info.getValue()} size="sm" variant="primary">Details</Button></div>;
            },
            enableSorting: false,
            enableColumnFilter: false,
        }),
    ]

    const reactTable = useReactTable({
        data: schools || [],
        columns,
        state: {
            sorting,
            columnFilters,
        },
        onSortingChange: setSorting,
        onColumnFiltersChange: setColumnFilters,

        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
    });

    useEffect(() => {
        if (!realSchools) return;
        const list = realSchools.map(item => {
            return {
                StatusString: GetOrganisationStatus(item.OrganisationStatusID),
                OrganisationName: item.OrganisationName,
                Street: item.Street + " " + item.HouseNo + ", " + item.PostCode + " " + item.City,
                Court: item.Court,
                CompRegNo: item.CompRegNo,
                ChamberMembership: item.ChamberMembership,
                FilenameBusinessLicense: item.FilenameBusinessLicense,
                IsInstitution: item.IsInstitution ? "Ja" : "",
                OrganisationID: item.OrganisationID
            };
        });

        setSchools(list);
    }, [realSchools]);

    if (schools === false) return <Loader />

    return (
        <Table striped>
            <thead>
                {reactTable.getHeaderGroups().map(headerGroup => (
                    <TableHeader key={headerGroup.id} table={reactTable} headerGroup={headerGroup} flexRender={flexRender} />
                ))}
            </thead>
            <tbody>
                {reactTable.getRowModel().rows.map(row => (
                    <tr key={row.id}>
                        {row.getVisibleCells().map(cell => {
                            return (
                                <td key={cell.id}>
                                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                </td>
                            )
                        })}
                    </tr>
                ))}
            </tbody>
        </Table>
    );
}

export default Schools;